import { Button } from '@repo/ui/components/ui/button'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import React, { Component, ReactNode } from 'react'
import { captureError } from '@/lib/sentry'
import { createErrorBoundaryProps } from '@/lib/utils/errors'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to Sentry if available
    captureError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    })

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const errorProps = createErrorBoundaryProps(this.state.error || undefined)

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 rounded-lg border border-destructive/30 bg-destructive/10 p-8 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden />
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">{errorProps.title}</h2>
            <p className="text-sm text-muted-foreground">
              {errorProps.description}
            </p>
            {errorProps.showDetails && errorProps.errorMessage && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm font-medium">
                  Error details (dev only)
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">
                  {errorProps.errorMessage}
                </pre>
              </details>
            )}
          </div>
          <Button onClick={this.handleRetry} variant="outline" size="sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}

// Hook version for functional components
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback} onError={onError}>
      <Component {...props} />
    </ErrorBoundary>
  )

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`
  return WrappedComponent
}

// Utility function for creating consistent error fallback UI
export function createErrorFallback(error?: Error) {
  const errorProps = createErrorBoundaryProps(error)

  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 rounded-lg border border-destructive/30 bg-destructive/10 p-8 text-center">
      <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden />
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">{errorProps.title}</h2>
        <p className="text-sm text-muted-foreground">
          {errorProps.description}
        </p>
        {errorProps.showDetails && errorProps.errorMessage && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm font-medium">
              Error details (dev only)
            </summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">
              {errorProps.errorMessage}
            </pre>
          </details>
        )}
      </div>
    </div>
  )
}
